/**
 * Weekly report screen v2 (owner-approved design, DEC-CHAT-20261010-403):
 * the six steps of one project's report for the week or period on screen.
 *
 * Pure: takes the shaped project (shape.js shapeReportProject) plus the few
 * things only the screen knows (a local "data confirmed" tick, a generation
 * this browser just started, an approve/publish in flight) and returns what to
 * draw -- each step done, current or locked, its meta, its help text and its
 * actions. Nothing here talks to the server; every action maps to an existing
 * call (confirm+generate = POST weekly, approve / render_pdf / publish /
 * reopen = PATCH weekly).
 *
 * The backend has no separate "confirm" write: the readiness hash the site
 * manager confirmed rides on the Generate call, and the server re-checks it
 * (422 data_incomplete on any change). Step 2 is therefore the screen's own
 * gate -- it freezes the hash the screen will send -- and becomes a stored fact
 * (DataConfirmedAtUtc / By) once the report exists.
 */

"use strict";

import { reportRef } from "./reportLabel.js";
import { formatDayShort, formatDayTime, formatStamp, whoLabel } from "./shape.js";

export const STEP_COUNT = 6;

export const STEP_TITLES = [
  "Check the data",
  "Confirm the data is complete",
  "Generate the report",
  "Review and edit the draft",
  "Approve",
  "Publish to the project site",
];

const HELP = [
  "Every line must be clear. Open a line to fix it, or accept an inspection without a photo.",
  "Confirming freezes the data for this period. Generate unlocks next.",
  "Builds a Word draft from the confirmed data.",
  "Open the draft in Word and make any changes. Edits are saved straight into the report.",
  "Approving makes a PDF of exactly this version.",
  "The client sees the PDF only.",
];

export const LOCK_NOTE_STEP1 = "Unlocks when step 1 is clear";

/** Items still to fix across the readiness lines (the sum of the red counts). */
export function itemsToFix(project) {
  return (project.checklist || []).reduce((sum, line) => sum + (line.green ? 0 : Number(line.count) || 0), 0);
}

/**
 * Is the screen's local "data confirmed" tick still valid for this project?
 * It freezes one readiness hash: any change to the data (a different hash), a
 * line turning red, or a different week/period invalidates it.
 */
export function confirmationValid(project, confirmed) {
  return Boolean(confirmed && confirmed.hash && confirmed.hash === project.readinessHash && project.allGreen);
}

/**
 * Which step is the current one (0-based; STEP_COUNT = everything done).
 * `ui` = { confirmed, generating, approving, publishing }.
 */
export function reachedStep(project, ui = {}) {
  if (ui.generating || project.generating) return 2;
  const current = project.current;
  if (current) {
    if (current.reportStatus === "Draft") return ui.approving ? 4 : 3;
    if (current.reportStatus === "Approved") {
      if (ui.publishing) return 5;
      const row = project.currentRow;
      return row && row.pdfFailed ? 4 : 5;
    }
    if (current.reportStatus === "Issued") return STEP_COUNT;
    return 3;
  }
  if (confirmationValid(project, ui.confirmed)) return 2;
  return project.allGreen ? 1 : 0;
}

/** "Step 4 of 6 · review the draft" and the like. */
export function stepSummary(project, ui = {}) {
  const at = reachedStep(project, ui);
  const current = project.current;
  if (ui.generating || project.generating) return "Step 3 of 6 · generating";
  if (at >= STEP_COUNT) {
    const stamp = current && current.issuedAtUtc ? ` ${formatDayShort(current.issuedAtUtc.slice(0, 10))}` : "";
    return `Done · published${stamp}`;
  }
  if (ui.approving) return "Step 5 of 6 · making the PDF";
  if (at === 0) {
    const n = itemsToFix(project);
    return `Step 1 of 6 · ${n} item${n === 1 ? "" : "s"} to fix`;
  }
  if (at === 1) return "Step 2 of 6 · confirm the data";
  if (at === 2) return "Step 3 of 6 · generate the report";
  if (at === 3) return "Step 4 of 6 · review the draft";
  if (at === 4) return "Step 5 of 6 · PDF needs a retry";
  return "Step 6 of 6 · ready to publish";
}

/**
 * The generation in progress for the view, from either this browser (just
 * clicked) or the backend's signal (after a reload).
 */
export function activeGeneration(project, local) {
  const g = local || project.generating || null;
  if (!g) return null;
  return {
    jobId: g.jobId || "",
    startedAt: g.startedAt || "",
    by: g.by || "",
    reportNumber: g.reportNumber || (project.current ? project.current.reportNumber : project.nextReportNumber),
  };
}

/** "Generating report #9 · started 09:24 by you". */
export function generatingTitle(gen, me, today) {
  const clock = gen.startedAt ? formatStamp(gen.startedAt, today) : "";
  const who = gen.by ? whoLabel(gen.by, me) : "you";
  return `Generating the new draft${clock ? ` · started ${clock}` : ""}${who ? ` by ${who}` : ""}`;
}

export const GENERATING_HELP =
  "The report is being built. The button stays off until it finishes, so it cannot start twice.";
export const GENERATING_DETAIL =
  "Collecting this period's inspections and photos · usually about 2 minutes. You can leave this page; the draft appears here when it is ready.";

function stepState(index, at) {
  if (index < at) return "done";
  if (index === at) return "current";
  return "locked";
}

/**
 * The six steps for one project.
 *
 * ui: { confirmed: {hash, atUtc, by}|null, generating: {...}|null (local),
 *       approving: bool, publishing: bool, me: upn, today: ISO date }
 *
 * Each step: { index, key, title, help, state, mark, meta, showLines,
 *   progress: null | {kind:"generating"|"approving"}, secondary, primary,
 *   primaryLocked, lockNote, action keys for the screen to wire }.
 */
export function buildSteps(project, ui = {}) {
  const at = reachedStep(project, ui);
  const today = ui.today || "";
  const me = ui.me || "";
  const current = project.current;
  const row = project.currentRow;
  const gen = activeGeneration(project, ui.generating);
  const confirmedLocal = confirmationValid(project, ui.confirmed) ? ui.confirmed : null;

  const version = row ? row.displayVersion : null;

  // Meta lines of the steps already passed (who and when).
  const confirmedAt = current && current.dataConfirmedAtUtc
    ? { atUtc: current.dataConfirmedAtUtc, by: current.dataConfirmedBy }
    : confirmedLocal
    ? { atUtc: confirmedLocal.atUtc, by: confirmedLocal.by }
    : gen && gen.startedAt
    ? { atUtc: gen.startedAt, by: gen.by }
    : null;
  const meta = {
    0: at > 0 ? "all clear" : "",
    1: at > 1 && confirmedAt ? [formatDayTime(confirmedAt.atUtc), whoLabel(confirmedAt.by, me)].filter(Boolean).join(" · ") : "",
    2: at > 2 && current && row ? `generated ${formatStamp(row.generatedAt, today)}` : "",
    3: at > 3 && row && row.editedAt ? `edited in Word ${formatStamp(row.editedAt, today)}` : "",
    4: at > 4 && current && current.approvedAtUtc ? `approved ${formatStamp(current.approvedAtUtc, today)}${row && row.pdfOpenUrl ? " · PDF ready" : ""}` : "",
    5: at > 5 && current && current.issuedAtUtc ? `published ${formatStamp(current.issuedAtUtc, today)}` : "",
  };

  const steps = STEP_TITLES.map((title, index) => {
    const state = stepState(index, at);
    const isCurrent = state === "current";
    const step = {
      index,
      key: ["check", "confirm", "generate", "review", "approve", "publish"][index],
      title,
      help: HELP[index],
      state,
      mark: state === "done" ? "✓" : String(index + 1),
      meta: meta[index] || (isCurrent ? "now" : state === "done" ? "" : "later"),
      showLines: index === 0 && state !== "done",
      progress: null,
      secondary: null,
      primary: null,
      primaryLocked: false,
      lockNote: "",
    };
    if (isCurrent) {
      if (index === 1) step.primary = "Confirm data complete";
      if (index === 2) step.primary = "Generate report";
      if (index === 3) {
        step.secondary = "Open draft in Word";
        step.primary = "Approve draft";
      }
      if (index === 4) step.primary = "Approve";
      if (index === 5) {
        step.secondary = "Open PDF";
        step.primary = "Publish PDF";
      }
    }
    return step;
  });

  // Step 1 is also the place its readiness lines show while it is not done.
  steps[0].showLines = at === 0;

  // Locked confirm: say why.
  if (at === 0) {
    steps[1].primary = "Confirm data complete";
    steps[1].primaryLocked = true;
    steps[1].lockNote = LOCK_NOTE_STEP1;
  }

  // Generating: the progress panel and a disabled button.
  if (gen) {
    const s = steps[2];
    s.help = GENERATING_HELP;
    s.progress = { kind: "generating", title: generatingTitle(gen, me, today), detail: GENERATING_DETAIL };
    s.primary = "Generating…";
    s.primaryLocked = true;
    s.secondary = null;
    s.meta = "now";
  }

  // PDF could not be made: step 5 carries the retry.
  if (at === 4 && current) {
    const s = steps[4];
    s.title = "Approve · PDF not created";
    s.help = `The report is approved, but the PDF conversion did not finish. Publishing needs the PDF.`;
    s.primary = "Retry PDF";
    s.secondary = null;
    s.meta = "now";
  }

  // Approving: the version is locked and the PDF is being made (step 5 is working).
  if (ui.approving && current) {
    const s = steps[4];
    s.help = HELP[4];
    s.progress = {
      kind: "approving",
      title: `Approved${version ? ` v${version}` : ""}${ui.approving.at ? ` · ${formatStamp(ui.approving.at, today)}` : ""}`,
      detail: "Usually under 30 seconds. The buttons stay off until it is done.",
    };
    s.primary = null;
    s.secondary = null;
    s.meta = "now";
  }

  // Publishing: the single publish call is in flight; every action is off.
  if (ui.publishing && current) {
    const s = steps[5];
    s.progress = { kind: "publishing", title: "Publishing the approved report…" };
    s.primary = "Publishing…";
    s.primaryLocked = true;
    s.secondary = null;
  }

  return {
    at,
    summary: stepSummary(project, ui),
    steps,
    version,
    label: current ? reportRef(current.reportStatus, current.publishedNumber, { capital: true }) : "",
  };
}

/** A short stamp for a dialog line: "Sat 10 Oct 09:14". */
export function longStamp(value) {
  return value ? formatDayTime(value) : "";
}
